// The signed-in session of transparent authentication.
//
// Storage stays where both apps already look: localStorage `tokens` = { access_token, id_token }
// (decoded by getUserDetails(), read by the axios interceptor and the region probe). This module adds
// the refresh token and the expiry next to them and owns every write, so the rest of the app keeps
// reading what it always read.
//
// Renewal replaces Auth0's hidden iframe: when the access token is about to expire it is exchanged
// through the API (POST /auth/refresh, rotating refresh tokens). One renewal runs at a time — in
// this tab (single flight) and across tabs (Web Locks) — and a tab that waited re-reads storage
// first, so it reuses what the other tab just obtained.
//
// A renewal that fails for a passing reason (network, our side) never ends the session: it is
// tried again with growing pauses. Only the API saying "this session is over" does.
import { useSyncExternalStore } from 'react';
import { Region } from '../regionBootstrap';
import { authApi, isAuthApiError, SessionPayload, SessionProfile } from './authApi';
import { currentAppKind, deploymentLeftTransparentMode, isTransparentAuth } from './authMode';

const TOKENS_KEY = 'tokens';
// Legacy app: role, organization and user details of the signed-in identity (see SessionProfile).
const PROFILE_KEY = 'dosen.auth.profile';
// Everything a previous identity leaves in storage (same list the impersonation hand-off clears).
const IDENTITY_KEYS = [
  TOKENS_KEY,
  PROFILE_KEY,
  'role',
  'v4UserId',
  'v4OrgId',
  'orgLocation',
  'username',
  'isAuthenticated',
  'lastseen',
  'persist:root-v4',
  'persist:root'
];
/** Renew this long before the access token expires (at most a fifth of its lifetime). */
const RENEW_BEFORE_MS = 5 * 60 * 1000;
/** A request never goes out with a token that expires within this margin (at most a tenth of its lifetime). */
const MIN_VALIDITY_MS = 60 * 1000;
/** Pause before a renewal that failed for a passing reason is attempted again; doubles up to the maximum. */
const RETRY_AFTER_MS = 5000;
const RETRY_MAX_MS = 60 * 1000;
// The app's own signed-out screens: a session that ends while one of them is open leaves the
// visitor where they are (an e-mailed link must not be lost to somebody's old session).
const SIGNED_OUT_SCREENS = /^#\/(login|accept-invitation|reset-password|forgot-password|sso)([/?]|$)/;
// Why the session ended, for the other tabs of this browser: they share the storage, not the API's
// answer. Written right before the session is cleared (with a stamp, so every end is a change the
// `storage` event reports), removed when a session is stored.
const ENDED_KEY = 'dosen.auth.ended';

export interface StoredTokens {
  access_token: string;
  id_token: string;
  refresh_token?: string | null;
  /** epoch milliseconds */
  expires_at?: number;
  /** epoch milliseconds: when this token set was stored (its lifetime = expires_at - issued_at) */
  issued_at?: number;
  /** the regional API that opened the session: renewals and sign-out go back to it */
  region?: Region | null;
}

export type SessionEndReason = 'expired' | 'disabled';

export function decodeJwt(token: string | undefined | null): Record<string, any> {
  try {
    const [, part] = (token || '').split('.');
    if (!part) return {};
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
    // UTF-8 bytes → text via percent-encoding (works without TextDecoder; names carry accents).
    const utf8 = binary
      .split('')
      .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join('');
    return JSON.parse(decodeURIComponent(utf8));
  } catch {
    return {};
  }
}

export function readTokens(): StoredTokens | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(TOKENS_KEY) || 'null');
    return parsed && typeof parsed.access_token === 'string' && typeof parsed.id_token === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export const expiresAtOf = (tokens: StoredTokens): number =>
  typeof tokens.expires_at === 'number' ? tokens.expires_at : (Number(decodeJwt(tokens.access_token).exp) || 0) * 1000;

export const hasSession = (): boolean => readTokens() !== null;

const subjectOf = (tokens: StoredTokens | null): string => String(decodeJwt(tokens?.id_token).sub ?? '');

// How close to its expiry a token is renewed / no longer sent: fixed margins for ordinary lifetimes,
// proportional for short ones (a fixed five minutes on a five-minute token would renew for ever).
function margins(tokens: StoredTokens): { renewBefore: number; minValidity: number } {
  const lifetime = typeof tokens.issued_at === 'number' ? expiresAtOf(tokens) - tokens.issued_at : Infinity;
  return {
    renewBefore: Math.max(0, Math.min(RENEW_BEFORE_MS, lifetime * 0.2)),
    minValidity: Math.max(0, Math.min(MIN_VALIDITY_MS, lifetime * 0.1))
  };
}

/** True when an expired access token can still be renewed without asking the person to sign in. */
export const canRenewSession = (): boolean => Boolean(readTokens()?.refresh_token);

// ---- change notification (this tab + other tabs) ------------------------------------------------

const listeners = new Set<() => void>();
// Bumped by changes that are not a change of the stored tokens (profile arrived, "not set up here").
let stateVersion = 0;
const notify = (): void => {
  listeners.forEach((listener) => listener());
};
const bump = (): void => {
  stateVersion += 1;
  notify();
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    // key === null: another tab called localStorage.clear() (sign-out).
    if (event.key === TOKENS_KEY || event.key === null) notify();
    // Another tab's session — this one's too — was ended by the API: this tab leaves the app the
    // same way, with the reason (a signed-out screen stays where it is, as always). Not announced
    // again: the tab that said it would answer back, and so on.
    if (isTransparentAuth() && event.key === ENDED_KEY && event.newValue) {
      const reason = event.newValue.split(':')[0] as SessionEndReason;
      if (reason === 'expired' || reason === 'disabled') endSession(reason, { announce: false });
    }
  });
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Snapshot for useSyncExternalStore: the same object as long as the stored value is unchanged.
let snapshotRaw: string | null = null;
let snapshotTokens: StoredTokens | null = null;
const snapshot = (): StoredTokens | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(TOKENS_KEY);
  } catch {
    /* storage unavailable */
  }
  if (raw !== snapshotRaw) {
    snapshotRaw = raw;
    snapshotTokens = readTokens();
  }
  return snapshotTokens;
};

const subscribeNever = (): (() => void) => () => undefined;

/**
 * The stored session, re-rendering the caller whenever it changes (sign-in, renewal, sign-out — in
 * any tab). In redirect mode nothing is subscribed to: Auth0's SDK owns the session there.
 */
export const useSessionTokens = (): StoredTokens | null =>
  useSyncExternalStore(isTransparentAuth() ? subscribe : subscribeNever, snapshot);

/** Changes when something about the session other than its tokens changed (see `bump`). */
export const useSessionVersion = (): number => useSyncExternalStore(subscribe, () => stateVersion);

// ---- a session this app's API does not hold ----------------------------------------------------------
//
// The two apps (new at one path, legacy at the other) share the browser's session. An account may
// exist in only one of them: the other one's API then answers `account_not_provisioned` — for a
// session that is perfectly good next door. This app must neither use it nor end it: it remembers
// (per tab, per identity) that the session is not its own and shows a screen that says so.

const notHereKey = (): string => `dosen.auth.notHere.${currentAppKind()}`;
let notHereSubject = '';

export function markSessionNotHere(): void {
  const subject = subjectOf(readTokens());
  if (!subject) return;
  notHereSubject = subject;
  try {
    sessionStorage.setItem(notHereKey(), subject);
  } catch {
    /* storage unavailable: remembered for this page load */
  }
  bump();
}

export function isSessionNotHere(): boolean {
  const subject = subjectOf(readTokens());
  if (!subject) return false;
  if (notHereSubject === subject) return true;
  try {
    return sessionStorage.getItem(notHereKey()) === subject;
  } catch {
    return false;
  }
}

function forgetNotHere(): void {
  notHereSubject = '';
  try {
    sessionStorage.removeItem(notHereKey());
  } catch {
    /* storage unavailable */
  }
}

// ---- writes ---------------------------------------------------------------------------------------

function storeProfile(idToken: string, profile: SessionProfile): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify({ sub: decodeJwt(idToken).sub, ...profile }));
  bump();
}

/** `region`: the regional API that opened (or renewed) the session. */
export function storeSession(session: SessionPayload, region?: Region | null): void {
  const now = Date.now();
  const tokens: StoredTokens = {
    access_token: session.accessToken,
    id_token: session.idToken,
    refresh_token: session.refreshToken,
    expires_at: now + Math.max(0, session.expiresIn) * 1000,
    issued_at: now,
    ...(region ? { region } : {})
  };
  // A session this app's API just handed out is this app's own.
  forgetNotHere();
  try {
    localStorage.removeItem(ENDED_KEY);
  } catch {
    /* storage unavailable */
  }
  // Profile first: whoever reacts to the new tokens must already find it.
  if (session.profile) storeProfile(session.idToken, session.profile);
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  notify();
}

/**
 * The stored profile — only when it belongs to the identity of the stored tokens, and only with
 * transparent authentication (with Auth0's own login the tokens' claims are all there is).
 */
export function readSessionProfile(): SessionProfile | null {
  if (!isTransparentAuth()) return null;
  try {
    const tokens = readTokens();
    const stored = JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null');
    return tokens && stored && stored.sub && stored.sub === decodeJwt(tokens.id_token).sub ? stored : null;
  } catch {
    return null;
  }
}

const PROFILE_ATTEMPTS = 3;
const pause = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

/**
 * Legacy app: makes sure the profile of the current session is at hand. A session opened through
 * the legacy API brings it along; one opened by the new app (same browser storage) does not, so it
 * is fetched once. An expired access token is renewed instead — the renewal brings the profile.
 * Throws when the profile could not be loaded for a passing reason: the caller must not go on
 * without it (it would take the person for somebody without a role).
 */
export async function ensureSessionProfile(): Promise<void> {
  for (let attempt = 1; ; attempt += 1) {
    const tokens = readTokens();
    if (!tokens || readSessionProfile() || isSessionNotHere()) return;
    try {
      const { profile } = await authApi.profile(tokens.access_token, tokens.id_token);
      // Still the same session (nobody signed out or in meanwhile)?
      if (readTokens()?.id_token === tokens.id_token) storeProfile(tokens.id_token, profile);
      return;
    } catch (err) {
      if (isAuthApiError(err) && err.code === 'account_not_provisioned') return markSessionNotHere();
      if (isAuthApiError(err) && err.status === 401) {
        await freshAccessToken({ force: true });
        return;
      }
      if (attempt >= PROFILE_ATTEMPTS) throw err;
      await pause(500 * attempt);
    }
  }
}

export function clearSession(): void {
  try {
    IDENTITY_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
  forgetNotHere();
  stopRetrying();
  notify();
}

/**
 * Leave the signed-in app for `target`. Everything held in memory (stores, caches, in-flight
 * requests) must go with the session, as it did on the round trip through Auth0 — and a target in
 * this same document only changes the hash, so the page is reloaded.
 */
export function leaveTo(target: string): void {
  leaving = true;
  const sameDocument = target.split('#')[0] === window.location.href.split('#')[0];
  window.location.replace(target);
  if (sameDocument) window.location.reload();
}

/**
 * Why the session ended a moment ago, for a tab that opens right after (its own storage already
 * cleared by then): the reason another tab left behind, while it is fresh and nobody signed in since.
 */
export function recentlyEndedReason(maxAgeMs = 15_000): SessionEndReason | null {
  if (!isTransparentAuth() || hasSession()) return null;
  try {
    const [reason, stamp] = (localStorage.getItem(ENDED_KEY) || '').split(':');
    if ((reason !== 'expired' && reason !== 'disabled') || Date.now() - Number(stamp) > maxAgeMs) return null;
    return reason;
  } catch {
    return null;
  }
}

// The page is on its way out (leaveTo): until the browser gets there, nothing in the app may start
// a navigation of its own — a route guard that sends a cleared session to /login would replace the
// address the page is leaving for (and its reason) before the reload lands there.
let leaving = false;
export const isLeaving = (): boolean => leaving;

/**
 * The session is over (the API said so): forget it and show the sign-in screen with the reason.
 * On one of the app's own signed-out screens the visitor simply stays where they are.
 */
export function endSession(reason: SessionEndReason, options: { announce?: boolean } = {}): void {
  const stays = SIGNED_OUT_SCREENS.test(window.location.hash);
  // Leaving: said before the session is cleared, so the guards that re-render on the clearing
  // render nothing instead of sending the page to a plain /login.
  if (!stays) leaving = true;
  // Told to the browser's other tabs — unless it came from one of them.
  if (options.announce !== false) {
    try {
      localStorage.setItem(ENDED_KEY, `${reason}:${Date.now()}`);
    } catch {
      /* storage unavailable */
    }
  }
  clearSession();
  if (stays) return;
  leaveTo(`${window.location.origin}${window.__APP_BASE__ || ''}/#/login?ended=${reason}`);
}

// ---- renewal --------------------------------------------------------------------------------------

let inFlight: Promise<string | null> | null = null;
let retryTimer: number | null = null;
let retryDelay = RETRY_AFTER_MS;

function stopRetrying(): void {
  if (retryTimer !== null) window.clearTimeout(retryTimer);
  retryTimer = null;
  retryDelay = RETRY_AFTER_MS;
}

// A renewal that failed for a passing reason is repeated on its own — nobody has to click anything.
function retryLater(): void {
  if (retryTimer !== null) return;
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    freshAccessToken({ force: true });
  }, retryDelay);
  retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
}

async function withTabLock<T>(task: () => Promise<T>): Promise<T> {
  const { locks } = navigator as any;
  return locks?.request ? locks.request('dosen-auth-renew', task) : task();
}

async function renew(seen: StoredTokens): Promise<string | null> {
  return withTabLock(async () => {
    const latest = readTokens();
    if (!latest) return null; // signed out meanwhile
    // Another tab renewed while this one waited for the lock: use its result.
    if (latest.refresh_token !== seen.refresh_token && expiresAtOf(latest) - Date.now() > margins(latest).minValidity) {
      notify();
      return latest.access_token;
    }
    if (!latest.refresh_token) return null;
    try {
      const session = await authApi.refresh(latest.refresh_token, subjectOf(latest) || undefined, latest.region);
      const current = readTokens();
      if (!current) {
        // Signed out while the renewal was on its way: nobody will ever use this session.
        if (session.refreshToken) authApi.logout(session.refreshToken, latest.region).catch(() => undefined);
        return null;
      }
      if (current.refresh_token !== latest.refresh_token) {
        // Another session was stored meanwhile (another tab renewed, or somebody signed in): it stands.
        return subjectOf(current) === subjectOf(latest) && expiresAtOf(current) > Date.now()
          ? current.access_token
          : null;
      }
      storeSession(session, latest.region);
      stopRetrying();
      return session.accessToken;
    } catch (err) {
      if (isAuthApiError(err) && (err.code === 'session_expired' || err.code === 'account_disabled')) {
        endSession(err.code === 'session_expired' ? 'expired' : 'disabled');
        return null;
      }
      // A good session — of an account this app's API does not hold. Left untouched (see above).
      if (isAuthApiError(err) && err.code === 'account_not_provisioned') {
        markSessionNotHere();
        return null;
      }
      // The endpoint itself is gone. When the API says the deployment went back to Auth0's own
      // login, this session cannot be renewed here any more. Only its explicit word counts: a
      // 404 out of a proxy during a deployment says nothing, and is retried like any passing failure.
      if (isAuthApiError(err) && err.status === 404 && (await deploymentLeftTransparentMode())) {
        endSession('expired');
        return null;
      }
      // Passing (network, 5xx, rate limit): the session is NOT discarded. The caller goes on with
      // what it has, if that is still good; the renewal is tried again shortly.
      retryLater();
      return expiresAtOf(latest) > Date.now() ? latest.access_token : null;
    }
  });
}

/**
 * A usable access token, renewing it first when it is expired or about to expire.
 * null = no token to use right now: there is no session (never had one, signed out, it just ended,
 * it is not this app's), or it could not be renewed yet (see `hasSession()` to tell these apart).
 */
export async function freshAccessToken(options: { force?: boolean } = {}): Promise<string | null> {
  const tokens = readTokens();
  if (!tokens || isSessionNotHere()) return null;
  const remaining = expiresAtOf(tokens) - Date.now();
  if (!options.force && remaining > margins(tokens).minValidity) return tokens.access_token;
  if (!tokens.refresh_token) return remaining > 0 ? tokens.access_token : null;
  // A retry is already scheduled: do not ask the API again for every request in between.
  if (!options.force && retryTimer !== null) return remaining > 0 ? tokens.access_token : null;
  if (!inFlight) {
    inFlight = renew(tokens).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Milliseconds until the session should be renewed proactively (0 = now, null = nothing to renew). */
export function renewDelayMs(): number | null {
  const tokens = readTokens();
  if (!tokens?.refresh_token || isSessionNotHere()) return null;
  return Math.max(0, expiresAtOf(tokens) - Date.now() - margins(tokens).renewBefore);
}

/**
 * Sign out: revoke the refresh token at the API (best effort — it may outlive the page) and forget
 * the session here. `refreshToken` (and the session's region) are passed in because callers clear
 * storage before calling.
 */
export function signOut(refreshToken: string | null | undefined, region?: Region | null): void {
  if (refreshToken) authApi.logout(refreshToken, region).catch(() => undefined);
  clearSession();
}
